// The life of each piece of damage on a vehicle, built from the marks placed on inspection photos.
//
// Every check records marks on its own photos, so one dent appears as a mark on check after check. A mark of the same
// type, on the same side of the vehicle, at (about) the same spot as an earlier one is the SAME damage seen again.
// Grouping them gives each piece of damage a first-seen date, how often it has been seen, whether it got worse, and
// whether (and when) it was repaired -- the "progression" Janelle wants to report on.
import { localIsoDay } from "@/lib/vehicleChecks";

/** Marks within this many percent of each other (on both axes) are the same spot. */
export const SAME_SPOT_PERCENT = 5;

export interface DamageSighting {
  id: string;
  inspection_id: string;
  /** When the inspection happened (ISO timestamp). */
  inspection_at: string;
  inspection_type: string | null;
  inspected_by: string;
  photo_id: string;
  /** e.g. exterior_front */
  photo_type: string;
  storage_path: string;
  x_percent: number;
  y_percent: number;
  damage_type: string;
  severity: string;
  notes: string | null;
  /** Set when this damage was recorded as repaired (YYYY-MM-DD). */
  repaired_on: string | null;
  repaired_by?: string | null;
  repair_notes?: string | null;
}

export type SeverityLevel = "minor" | "moderate" | "severe" | "unknown";
export type SeverityTrend = "worse" | "better" | "same";
export type DamageStatus = "present" | "repaired";

export interface DamageItem {
  /** Stable id: the first mark's id. */
  key: string;
  view: string;
  damageType: string;
  /** Where it was first marked. */
  x: number;
  y: number;
  /** Every sighting, oldest first. */
  sightings: DamageSighting[];
  firstSeenAt: string;
  lastSeenAt: string;
  firstSeverity: SeverityLevel;
  latestSeverity: SeverityLevel;
  severityTrend: SeverityTrend;
  status: DamageStatus;
  repairedOn: string | null;
  repairedBy: string | null;
  /** Marked again on a check after it was recorded as repaired. */
  reappeared: boolean;
  /** Distinct notes in the order they were written. */
  notes: string[];
  /** Ids of every mark in this damage (what a repair is recorded against). */
  markerIds: string[];
}

export const normalizeSeverity = (s: string | null | undefined): SeverityLevel => {
  const v = (s ?? "").trim().toLowerCase();
  return v === "minor" || v === "moderate" || v === "severe" ? v : "unknown";
};

const SEVERITY_RANK: Record<SeverityLevel, number> = { unknown: 0, minor: 1, moderate: 2, severe: 3 };

export const severityLabel = (s: SeverityLevel | string | null | undefined): string => {
  const v = normalizeSeverity(s as string);
  return v === "unknown" ? "" : v.charAt(0).toUpperCase() + v.slice(1);
};

const VIEW_LABELS: Record<string, string> = {
  exterior_front: "Front",
  exterior_rear: "Rear",
  exterior_driver: "Driver side",
  exterior_passenger: "Passenger side",
  oil_dipstick: "Oil dipstick",
  odometer: "Odometer",
};

export const viewLabel = (photoType: string): string => VIEW_LABELS[photoType] ?? photoType.replace(/_/g, " ");

const sameType = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

const sameSpot = (a: DamageSighting, b: DamageSighting) =>
  Math.abs(a.x_percent - b.x_percent) < SAME_SPOT_PERCENT && Math.abs(a.y_percent - b.y_percent) < SAME_SPOT_PERCENT;

/**
 * Groups sightings into pieces of damage. `timeZone` decides which calendar day an inspection counts as for
 * comparing with a repair date.
 */
export function buildDamageRegister(sightings: DamageSighting[], timeZone?: string): DamageItem[] {
  const ordered = [...sightings].sort((a, b) => new Date(a.inspection_at).getTime() - new Date(b.inspection_at).getTime());
  const groups: DamageSighting[][] = [];

  for (const s of ordered) {
    const home = groups.find((g) => {
      const last = g[g.length - 1];
      return last.photo_type === s.photo_type && sameType(last.damage_type, s.damage_type) && sameSpot(last, s);
    });
    if (home) home.push(s);
    else groups.push([s]);
  }

  const items = groups.map((g): DamageItem => {
    const first = g[0];
    const last = g[g.length - 1];
    const dayOf = (ts: string) => localIsoDay(ts, timeZone);

    const repairedOn = g.reduce<string | null>((latest, s) => (s.repaired_on && (!latest || s.repaired_on > latest) ? s.repaired_on : latest), null);
    const repairMark = repairedOn ? g.find((s) => s.repaired_on === repairedOn) : undefined;
    // Marked again on a day after the repair: the damage is back.
    const reappeared = !!repairedOn && g.some((s) => dayOf(s.inspection_at) > repairedOn);

    const firstSeverity = normalizeSeverity(first.severity);
    const latestSeverity = normalizeSeverity(last.severity);
    const trend: SeverityTrend =
      SEVERITY_RANK[latestSeverity] > SEVERITY_RANK[firstSeverity] ? "worse" : SEVERITY_RANK[latestSeverity] < SEVERITY_RANK[firstSeverity] ? "better" : "same";

    const notes: string[] = [];
    for (const s of g) {
      const n = (s.notes ?? "").trim();
      if (n && !notes.includes(n)) notes.push(n);
    }

    return {
      key: first.id,
      view: first.photo_type,
      damageType: first.damage_type,
      x: first.x_percent,
      y: first.y_percent,
      sightings: g,
      firstSeenAt: first.inspection_at,
      lastSeenAt: last.inspection_at,
      firstSeverity,
      latestSeverity,
      severityTrend: trend,
      status: repairedOn && !reappeared ? "repaired" : "present",
      repairedOn,
      repairedBy: repairMark?.repaired_by ?? null,
      reappeared,
      notes,
      markerIds: g.map((s) => s.id),
    };
  });

  // Still-there damage first, then the most recently seen.
  return items.sort((a, b) => {
    if (a.status !== b.status) return a.status === "present" ? -1 : 1;
    return new Date(b.lastSeenAt).getTime() - new Date(a.lastSeenAt).getTime();
  });
}

/** True when this sighting is the first time that piece of damage was marked. */
export const isFirstSighting = (item: DamageItem, sighting: DamageSighting): boolean => item.sightings[0]?.id === sighting.id;

/** Looks up which piece of damage a sighting belongs to. */
export function itemBySighting(items: DamageItem[]): Map<string, DamageItem> {
  const map = new Map<string, DamageItem>();
  for (const item of items) for (const s of item.sightings) map.set(s.id, item);
  return map;
}

/** A short plain-words status, e.g. "Still there", "Repaired 12/11/2026", "Came back after repair". */
export function damageStatusText(item: DamageItem, formatDay: (iso: string) => string): string {
  if (item.status === "repaired") return `Repaired ${formatDay(item.repairedOn!)}`;
  if (item.reappeared) return "Came back after repair";
  return "Still there";
}
